import React from "react";

function isSafeHref(href) {
  return /^(https?:\/\/|mailto:)/i.test(href);
}

const MD_LINK = /\[([^\]]+)\]\(((?:https?:\/\/|mailto:)[^)\s|]+)((?:\|#[0-9a-fA-F]{3,8})*)\)/g;

function linkColorFromMatch(suffix) {
  const colors = String(suffix || "").match(/#[0-9a-fA-F]{3,8}/g);
  return colors && colors.length ? colors[colors.length - 1] : undefined;
}

function stripColorSuffixes(href) {
  return String(href || "").replace(/(\|#([0-9a-fA-F]{3,8}))+$/g, "");
}

function Link({ href, children, color }) {
  const external = /^https?:\/\//i.test(href);
  return (
    <a
      className="edit-inline-link"
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      style={color ? { color, textDecoration: "underline" } : undefined}
    >
      {children}
    </a>
  );
}

function autolink(text, keyPrefix) {
  const nodes = [];
  const re = /(https?:\/\/[^\s]+)|([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    if (match[1]) {
      const href = match[1].replace(/[),.;]+$/, "");
      const trailing = match[1].slice(href.length);
      nodes.push(
        <Link key={`${keyPrefix}-u-${i++}`} href={href}>
          {href}
        </Link>
      );
      if (trailing) nodes.push(trailing);
    } else {
      const email = match[2];
      nodes.push(
        <Link key={`${keyPrefix}-e-${i++}`} href={`mailto:${email}`}>
          {email}
        </Link>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function cleanLinkLabel(label) {
  const hexes = [];
  const re = /\{#([0-9a-fA-F]{3,8})\}/g;
  let m;
  while ((m = re.exec(label)) !== null) hexes.push(`#${m[1]}`);
  return {
    label: String(label)
      .replace(/\{#[0-9a-fA-F]{3,8}\}/g, "")
      .replace(/\{\/\}/g, ""),
    color: hexes.length ? hexes[hexes.length - 1] : undefined,
  };
}

function extractLinks(text) {
  const links = [];
  const md = new RegExp(MD_LINK.source, "g");
  const body = String(text).replace(md, (full, label, href, suffix) => {
    const cleaned = cleanLinkLabel(label);
    links.push({
      label: cleaned.label,
      href: stripColorSuffixes(href),
      color: cleaned.color || linkColorFromMatch(suffix),
    });
    return `\u0000L${links.length - 1}\u0000`;
  });
  return { body, links };
}

function restoreLinksHtml(escaped, links) {
  return escaped.replace(/\u0000L(\d+)\u0000/g, (full, idx) => {
    const item = links[Number(idx)];
    if (!item || !isSafeHref(item.href)) return full;
    const color = item.color ? ` style="color:${item.color}"` : "";
    return `<a class="edit-inline-link" href="${escapeHtml(item.href)}"${color}>${escapeWithBreaks(item.label)}</a>`;
  });
}

function restoreLinksNodes(text, links, keyPrefix) {
  if (text == null || text === "") return [];
  const parts = String(text).split(/\u0000L(\d+)\u0000/);
  const nodes = [];
  for (let i = 0; i < parts.length; i += 1) {
    if (i % 2 === 1) {
      const item = links[Number(parts[i])];
      if (item && isSafeHref(item.href)) {
        nodes.push(
          <Link key={`${keyPrefix}-l-${i}`} href={item.href} color={item.color}>
            {item.label}
          </Link>
        );
      }
    } else if (parts[i]) {
      nodes.push(...autolink(parts[i], `${keyPrefix}-t-${i}`));
    }
  }
  return nodes;
}

function renderColoredNodes(text, links, keyPrefix) {
  if (text == null || text === "") return [];
  const nodes = [];
  const colorRe = /\{#([0-9a-fA-F]{3,8})\}([\s\S]*?)\{\/\}/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = colorRe.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(...restoreLinksNodes(text.slice(last, match.index), links, `${keyPrefix}p${i}`));
    }
    nodes.push(
      <span key={`${keyPrefix}c-${i}`} style={{ color: `#${match[1]}` }}>
        {renderColoredNodes(match[2], links, `${keyPrefix}n${i}`)}
      </span>
    );
    i += 1;
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push(...restoreLinksNodes(text.slice(last), links, `${keyPrefix}p${i}`));
  }
  return nodes;
}

function coloredHtml(text, links) {
  if (!text) return "";
  let html = "";
  const colorRe = /\{#([0-9a-fA-F]{3,8})\}([\s\S]*?)\{\/\}/g;
  let last = 0;
  let match;
  while ((match = colorRe.exec(text)) !== null) {
    html += restoreLinksHtml(escapeWithBreaks(text.slice(last, match.index)), links);
    html += `<span style="color:#${match[1]}">${coloredHtml(match[2], links)}</span>`;
    last = match.index + match[0].length;
  }
  html += restoreLinksHtml(escapeWithBreaks(text.slice(last)), links);
  return html;
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeWithBreaks(s) {
  return escapeHtml(s).replace(/\n/g, "<br>");
}

export function markupToHtml(text) {
  if (text == null || text === "") return "";
  const { body, links } = extractLinks(text);
  return coloredHtml(body, links);
}

export function rgbToHex(value) {
  if (value == null) return "";
  const raw = String(value).trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  const rgb = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!rgb) return "";
  return `#${[rgb[1], rgb[2], rgb[3]]
    .map((n) => Number(n).toString(16).padStart(2, "0"))
    .join("")}`;
}

function flattenText(node) {
  if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || "";
  if (node.nodeType !== Node.ELEMENT_NODE) return "";
  if (node.tagName.toLowerCase() === "br") return "\n";
  return Array.from(node.childNodes).map(flattenText).join("");
}

function firstDescendantColor(el) {
  const colored = el.querySelectorAll("span, font");
  for (const child of colored) {
    const hex = rgbToHex(child.style.color || child.getAttribute("color") || "");
    if (hex) return hex;
  }
  return "";
}

export function htmlToMarkup(root) {
  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.nodeValue || "";
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    const tag = node.tagName.toLowerCase();
    if (tag === "br") return "\n";
    const inner = Array.from(node.childNodes).map(walk).join("");
    if (tag === "a") {
      const href = stripColorSuffixes(node.getAttribute("href") || "");
      if (!isSafeHref(href)) return flattenText(node);
      const color = rgbToHex(node.style.color);
      const label = flattenText(node);
      const linkColor = color || firstDescendantColor(node);
      return linkColor ? `[${label}](${href}|${linkColor})` : `[${label}](${href})`;
    }
    if (tag === "span" || tag === "font") {
      const color = rgbToHex(node.style.color || node.getAttribute("color") || "");
      if (color) return `{#${color.slice(1)}}${inner}{/}`;
      return inner;
    }
    if (tag === "div" || tag === "p") {
      return `${inner}\n`;
    }
    return inner;
  }
  return Array.from(root.childNodes).map(walk).join("").replace(/\n+$/, "");
}

export function renderRichText(text) {
  if (text == null || text === "") return null;
  const { body, links } = extractLinks(text);
  return renderColoredNodes(body, links, "r");
}
